import React, { useEffect } from 'react';
import styles from './style.module.css';
import { StoreDetailsCard } from '../../../../marketPlace/components/storeDetailsCard';
import { EditIcon } from '../../../../assets/icons/editIcon';
import { useNavigate } from 'react-router-dom';
import Typography from '../../../../components/Typography/Typography';
import { useGetCurrentUserQuery } from '../../../../redux/services/userApi';
import { useGetStoreByOwnerQuery } from '../../../../redux/services/storesApi';

export const SellerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const defaultPlaceholderImage = 'https://placehold.co/210x121/';
  const defaultAvatarImage = 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?q=80&w=200&h=200&auto=format&fit=crop';

  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUser,
  } = useGetCurrentUserQuery('', {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: storesData,
    isLoading: isStoresLoading,
    error: storesError,
    refetch: refetchStores,
  } = useGetStoreByOwnerQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    refetchUser();
    refetchStores();
  }, [refetchUser, refetchStores]);

  const handleEditClick = () => {
    navigate('/profile/edit');
  };

  const formatJoinDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `Joined ${month} ${year}`;
    } catch (error) {
      return 'Joined Recently';
    }
  };

  const calculateStoreStats = (store: any) => {
    return {
      itemCount: 0, 
      rating: '0.0', 
      price: 0,
    };
  };

  const getAvatarUrl = (photoUrl: string | null) => {
    if (!photoUrl) return defaultAvatarImage;
    
    const timestamp = new Date().getTime();
    const separator = photoUrl.includes('?') ? '&' : '?';
    return `${photoUrl}${separator}t=${timestamp}`;
  };

  if (isUserLoading || isStoresLoading) {
    return (
      <div className={styles.sellerProfilePage}>
        <section className={styles.profileSection}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Typography variant="heading5" fontFamily="satoshi">
              Loading profile...
            </Typography>
          </div>
        </section>
      </div>
    );
  }

  if (userError || storesError) {
    const errorMessage = userError 
      ? 'error' in userError 
        ? userError.error 
        : 'status' in userError 
          ? `Error: ${userError.status}`
          : 'Failed to load profile'
      : 'Failed to load stores';

    return (
      <div className={styles.sellerProfilePage}>
        <section className={styles.profileSection}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Typography 
              variant="heading5" 
              fontFamily="satoshi" 
              className={styles.errorMessage}
            >
              Error Loading Profile
            </Typography>
            <Typography variant="bodyRegular" fontFamily="satoshi">
              {errorMessage}
            </Typography>
            <button 
              onClick={() => {
                refetchUser();
                refetchStores();
              }}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                background: '#1D1D1C',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: 'Satoshi',
              }}
            >
              Retry
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  return (
    <div className={styles.sellerProfilePage}>
      <section className={styles.profileSection}>
        <button onClick={handleEditClick} className={styles.editButton}>
          Edit <EditIcon />
        </button>
        <img
          src={getAvatarUrl(userData.photo_url)}
          alt={userData.full_name}
          className={styles.avatar}
          key={userData.updated_at}
        />
        <Typography 
          variant="heading4"
          fontFamily="satoshi"
          className={styles.profileName}
        >
          {userData.full_name}
        </Typography>
        {userData.display_name && (
          <Typography 
            variant="heading6"
            fontFamily="satoshi"
            className={styles.storeName}
          >
            {userData.display_name}
          </Typography>
        )}
        {userData.additional_info ? (
          <Typography 
            variant="bodyRegular"
            fontFamily="satoshi"
            className={styles.bio}
          >
            {userData.additional_info}
          </Typography>
        ) : (
          <Typography 
            variant="bodyRegular"
            fontFamily="satoshi"
            className={styles.additional_info}
          >
            No bio available. Click Edit to add.
          </Typography>
        )}
        <div>
          <Typography
            variant="bodyRegular"
            fontFamily="satoshi"
            className={styles.profileMeta}
          >
            <span>{userData.email}</span>
            <span className={styles.dotSeparator}>&bull;</span>
            <span>{formatJoinDate(userData.created_at)}</span>
          </Typography>
        </div>
      </section>

      <section className={styles.storesSection}>
        <div className={styles.storesContainer}>
          <Typography 
            variant="heading4"
            fontFamily="satoshi" 
            className={styles.sectionTitle}
          >
            My Stores
          </Typography>
          
          {storesData && storesData.length > 0 ? (
            <div className={styles.storesGrid}>
              {storesData.map((store) => {
                const stats = calculateStoreStats(store);
                return (
                  <StoreDetailsCard
                    key={store._id}
                    storePreview={store.logo_url || defaultPlaceholderImage}
                    storeName={store.name}
                    storeRating={stats.rating}
                    storeCreationDate={formatJoinDate(store.created_at)}
                    storeItemCount={stats.itemCount}
                    storeLocation={store.location}
                    storePrice={stats.price}
                    storeId={store._id}
                    onClick={() => {
                      navigate(`/store/${store.slug}`);
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Typography 
                variant="bodyRegular" 
                fontFamily="satoshi"
              >
                You haven't created any stores yet.
              </Typography>
              <button
                onClick={() => navigate('/create-store')}
                style={{
                  padding: '12px 24px',
                  background: '#1D1D1C',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Satoshi',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Create Your First Store
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};